// Display formatting shared by the mobile and desktop views. Numbers arrive
// unrounded from the API; rounding happens only here.

const MINUS = '−'

export function fmtPct(v: number | null | undefined, digits = 1): string {
  if (v == null || !Number.isFinite(v)) return '—'
  const sign = v > 0 ? '+' : v < 0 ? MINUS : ''
  return `${sign}${Math.abs(v).toFixed(digits)}%`
}

// A difference of two percentages, e.g. a portfolio's lead over its benchmark.
export function fmtPts(v: number | null | undefined, digits = 1): string {
  if (v == null || !Number.isFinite(v)) return '—'
  const sign = v > 0 ? '+' : v < 0 ? MINUS : ''
  return `${sign}${Math.abs(v).toFixed(digits)} pts`
}

export function fmtUsd(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—'
  const sign = v > 0 ? '+' : v < 0 ? MINUS : ''
  return `${sign}$${Math.abs(v).toFixed(2)}`
}

export function fmtPrice(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—'
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// Calendar dates in US Eastern time: a session's ts is its 09:30 ET open, and
// an episode published at 22:00 ET belongs to that evening, not the next day.
const ET = 'America/New_York'
const shortDate = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: ET })
const longDate = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: ET })
const monthYear = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: ET })

export function fmtDate(iso: string): string {
  return shortDate.format(new Date(iso))
}

export function fmtDateLong(iso: string): string {
  return longDate.format(new Date(iso))
}

export function fmtMonthYear(iso: string): string {
  return monthYear.format(new Date(iso))
}

// "2h", "3d", "5w" — the design's "{ago} ago".
export function timeAgo(iso: string, now: Date = new Date()): string {
  const s = Math.max(0, (now.getTime() - new Date(iso).getTime()) / 1000)
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m`
  if (s < 86400) return `${Math.floor(s / 3600)}h`
  if (s < 86400 * 14) return `${Math.floor(s / 86400)}d`
  if (s < 86400 * 365) return `${Math.floor(s / (86400 * 7))}w`
  return `${Math.floor(s / (86400 * 365))}y`
}

export function daysSince(iso: string, now: Date = new Date()): number {
  return Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 86400000))
}

// "2h 38m" / "41m".
export function fmtDuration(seconds: number | null | undefined): string {
  if (seconds == null) return ''
  const h = Math.floor(seconds / 3600)
  const m = Math.round((seconds % 3600) / 60)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

// "1:02:14" / "50:22" — a position in an episode.
export function fmtClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = String(s % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`
}

export type Dir = 'BUY' | 'SELL' | 'HOLD'
export type Kind = 'up' | 'down' | 'hold'

export function dirOf(type: string | null): Dir | null {
  return type === 'buy' ? 'BUY' : type === 'sell' ? 'SELL' : type === 'hold' ? 'HOLD' : null
}

// Marker/pill colour for a call.
export function kindOf(type: string | null): Kind {
  return type === 'sell' ? 'down' : type === 'hold' ? 'hold' : 'up'
}

// Signed colour for a number: green for gains, red for losses.
export function signClass(v: number | null | undefined): '' | 'up' | 'down' {
  if (v == null || v === 0) return ''
  return v > 0 ? 'up' : 'down'
}

export function toTargetPct(target: number | null, last: number | null): number | null {
  if (target == null || last == null || last === 0) return null
  return (target / last - 1) * 100
}

// A ticker whose newest close is older than a long weekend plus the nightly
// fetch (renamed, delisted or acquired) shows when it was last priced.
export function isStale(lastTs: string | null, now: Date = new Date()): boolean {
  if (!lastTs) return false
  return now.getTime() - new Date(lastTs).getTime() > 5 * 86400000
}
